import React, { useState, useEffect, useContext } from 'react'
import { AuthContext } from '../../Context'
import { AuthContextApi } from '../../Context/api'
import { toast } from 'react-toastify'
import {
    ChartBarIcon,
    UsersIcon,
    CubeIcon,
    TruckIcon,
    BuildingOfficeIcon,
    CurrencyDollarIcon,
    ArrowTrendingUpIcon,
    ClockIcon,
    CalendarIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    PlusIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    StarIcon,
    HeartIcon,
    BoltIcon,
    SparklesIcon
} from '@heroicons/react/24/outline'
import './styles.css'

const Dashboard = () => {
    const { states, setters } = useContext(AuthContext)
    const { checkLogin, getDashBoardataSistema } = useContext(AuthContextApi)

    // Estados locais
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedFilterField, setSelectedFilterField] = useState('nome')
    const [selectedSortField, setSelectedSortField] = useState('data_criacao')
    const [sortDirection, setSortDirection] = useState('desc')
    const [recordsPerPage, setRecordsPerPage] = useState(10)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [selectedItem, setSelectedItem] = useState(null)

    // Estado para dados do dashboard
    const [dashboardStats, setDashboardStats] = useState([])

    // Função para mapear ícones baseado no nome
    const getIconByType = (iconType) => {
        switch (iconType) {
            case 'box': return CubeIcon
            case 'truck': return TruckIcon
            case 'users': return UsersIcon
            case 'check-circle': return CheckCircleIcon
            case 'building': return BuildingOfficeIcon
            case 'clock': return ClockIcon
            default: return CubeIcon
        }
    }

    // Função para mapear cores baseado no nome
    const getColorByType = (colorType) => {
        switch (colorType) {
            case 'blue': return 'blue'
            case 'green': return 'green'
            case 'orange': return 'orange'
            case 'purple': return 'purple'
            case 'red': return 'red'
            case 'yellow': return 'yellow'
            default: return 'blue'
        }
    }

    const mockRecentActivity = [
        {
            id: 1,
            type: 'movimentacao',
            title: 'Movimentação de Produto',
            description: 'Produto "iPhone 15 Pro" movimentado para Cliente ABC',
            time: '2 minutos atrás',
            status: 'success',
            icon: TruckIcon
        },
        {
            id: 2,
            type: 'produto',
            title: 'Novo Produto Cadastrado',
            description: 'MacBook Pro M3 adicionado ao estoque',
            time: '15 minutos atrás',
            status: 'success',
            icon: CubeIcon
        },
        {
            id: 3,
            type: 'cliente',
            title: 'Novo Cliente',
            description: 'Empresa XYZ cadastrada como cliente',
            time: '1 hora atrás',
            status: 'success',
            icon: UsersIcon
        },
        {
            id: 4,
            type: 'alerta',
            title: 'Estoque Baixo',
            description: 'Produto "Samsung Galaxy S24" com estoque baixo',
            time: '2 horas atrás',
            status: 'warning',
            icon: ExclamationTriangleIcon
        },
        {
            id: 5,
            type: 'movimentacao',
            title: 'Retorno de Produto',
            description: 'Produto "iPad Pro" retornado ao fornecedor',
            time: '3 horas atrás',
            status: 'info',
            icon: TruckIcon
        }
    ]

    const mockQuickActions = [
        {
            id: 1,
            title: 'Cadastrar Produto',
            description: 'Adicionar novo produto ao estoque',
            icon: PlusIcon,
            color: 'blue',
            href: '/produtos'
        },
        {
            id: 2,
            title: 'Nova Movimentação',
            description: 'Criar movimentação de produto',
            icon: TruckIcon,
            color: 'green',
            href: '/movimentacoes'
        },
        {
            id: 3,
            title: 'Cadastrar Cliente',
            description: 'Adicionar novo cliente',
            icon: UsersIcon,
            color: 'purple',
            href: '/entidades'
        }
    ]

    // Opções de filtro
    const filterOptions = [
        { value: 'nome', label: 'Nome' },
        { value: 'tipo', label: 'Tipo' },
        { value: 'status', label: 'Status' },
        { value: 'data_criacao', label: 'Data de Criação' }
    ]

    // Opções de ordenação
    const sortOptions = [
        { value: 'data_criacao', label: 'Data de Criação' },
        { value: 'nome', label: 'Nome' },
        { value: 'tipo', label: 'Tipo' },
        { value: 'status', label: 'Status' }
    ]

    // Função para buscar
    const handleSearch = () => {
        console.log('Realizando busca com:', { searchTerm, selectedFilterField })
        toast.info('Busca realizada com sucesso!')
    }

    // Função para limpar busca
    const handleClear = () => {
        setSearchTerm('')
        setSelectedFilterField('nome')
        setSelectedSortField('data_criacao')
        setSortDirection('desc')
        setRecordsPerPage(10)
        toast.info('Filtros limpos!')
    }

    // Função para abrir modal
    const openModal = (item = null) => {
        setIsEditMode(!!item)
        setSelectedItem(item)
        setIsModalOpen(true)
    }

    // Função para fechar modal
    const closeModal = () => {
        setIsModalOpen(false)
        setIsEditMode(false)
        setSelectedItem(null)
    }

    // Função para obter ícone do status
    const getStatusIcon = (status) => {
        switch (status) {
            case 'success': return CheckCircleIcon
            case 'warning': return ExclamationTriangleIcon
            case 'info': return InformationCircleIcon
            default: return InformationCircleIcon
        }
    }


    // Função para carregar dados do dashboard
    const loadDashboardData = async () => {
        try {
            const response = await getDashBoardataSistema()
            if (response && response.status) {
                setDashboardStats(response.data)
                toast.success('Dashboard carregado com sucesso!')
            }
        } catch (error) {
            console.error('Erro ao carregar dados do dashboard:', error)
            toast.error('Erro ao carregar dados do dashboard')
        }
    }

    // useEffect para carregar dados iniciais
    useEffect(() => {
        checkLogin()
        loadDashboardData()
    }, [])

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                        <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl shadow-lg">
                            <ChartBarIcon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                                Dashboard
                            </h1>
                            <p className="text-gray-600">Visão geral do sistema Experts Control</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <div className="text-right">
                            <p className="text-sm text-gray-600">Bem-vindo de volta,</p>
                            <p className="font-semibold text-gray-900">
                                {localStorage.getItem('usuario') || 'Usuário'}
                            </p>
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            <UsersIcon className="w-5 h-5 text-white" />
                        </div>
                    </div>
                </div>

                {/* Dashboard Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                    {dashboardStats && dashboardStats.length > 0 ? (
                        dashboardStats.map((stat, index) => {
                            const IconComponent = getIconByType(stat.icon)
                            const colorClass = getColorByType(stat.color)
                            return (
                                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 stats-card dashboard-card animate-fade-in-up" style={{ animationDelay: `${index * 100}ms` }}>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className={`p-3 rounded-full bg-${colorClass}-100 icon-hover`}>
                                            <IconComponent className={`w-6 h-6 text-${colorClass}-600`} />
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-900 number-animation">{stat.value}</p>
                                        <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                                    </div>
                                </div>
                            )
                        })
                    ) : (
                        // Loading state ou fallback
                        <div className="col-span-full text-center py-12">
                            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Carregando dados do dashboard...</h3>
                            <p className="text-gray-500">Aguarde enquanto buscamos as informações do sistema.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Quick Actions */}
            <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Ações Rápidas</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {mockQuickActions.map((action, index) => {
                        const IconComponent = action.icon
                        return (
                            <button
                                key={action.id}
                                onClick={() => window.location.href = action.href}
                                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-left group quick-action animate-fade-in-scale" style={{ animationDelay: `${index * 150}ms` }}
                            >
                                <div className={`p-3 rounded-full bg-${action.color}-100 w-fit mb-4 group-hover:scale-110 transition-transform duration-300 icon-hover`}>
                                    <IconComponent className={`w-6 h-6 text-${action.color}-600`} />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{action.title}</h3>
                                <p className="text-sm text-gray-600">{action.description}</p>
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default Dashboard